import {
  Cloud,
  Database,
  Landmark,
  Leaf,
  ShieldCheck,
  type LucideIcon
} from 'lucide-react';

export const CASE_CATEGORIES = [
  'All',
  'Migration',
  'Data Engineering',
  'Cloud Infrastructure',
  'Streaming & AI',
  'Banking & Finance'
] as const;

export type CaseCategory = Exclude<(typeof CASE_CATEGORIES)[number], 'All'>;

export type CaseMetric = {
  label: string;
  value: string;
};

export type CaseStudy = {
  id: string;
  category: CaseCategory;
  title: string;
  challenge: string;
  solution: string;
  metrics: CaseMetric[];
  tags: string[];
  icon: LucideIcon;
  image?: string;
  websiteUrl?: string;
  placeholderLabel?: string;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: 'cropt-satellite-ml-pipeline',
    category: 'Data Engineering',
    title: 'High-Volume Sat-Imagery Lakehouse for Cropt AgTech',
    challenge:
      'Processing petabytes of multi-spectral satellite imagery and geospatial data to support agricultural analytics and predictive ML models.',
    solution:
      'Built a scalable Delta Lakehouse platform that automated satellite data ingestion, transformation, and storage, enabling efficient crop monitoring, feature engineering, and machine learning workflows.',
    metrics: [
      { label: 'Training Speed', value: '15x' },
      { label: 'SLA Standard', value: '99.9%' }
    ],
    tags: [
      'AgTech Lakehouse',
      'Satellite Ingestion',
      'Model Accelerate',
      'GeoJSON Layers',
      'Delta Lake'
    ],
    icon: Leaf,
    image: '/images/cropt.png',
    websiteUrl: 'https://cropt.ag/'
  },
  {
    id: 'inspira-group-cloud-repatriation',
    category: 'Cloud Infrastructure',
    title: 'E-Commerce Cloud Repatriation and Cost Optimization',
    challenge:
      'Keeping analytics, reporting, and event processing stable across a growing portfolio of high-traffic digital products while controlling infrastructure spend and delivery risk.',
    solution:
      'Maintained and optimized the data platform around DataSliv, Kafka streams, and batch analytics so product teams could rely on fresher reporting and predictable infrastructure costs.',
    metrics: [
      { label: 'Cost Reduction', value: '18x' },
      { label: 'Workload Parity', value: '100%' }
    ],
    tags: [
      'Cloud Repatriation',
      'DataSliv',
      'E-Commerce BI',
      'Kafka Streams',
      'HDFS Storage'
    ],
    icon: Cloud,
    image: '/images/inspira.png',
    websiteUrl: 'https://www.inspiragrupa.com/en/'
  },
  {
    id: 'design-software-dwh-analytics-optimization',
    category: 'Data Engineering',
    title:
      'Data Engineering and Analytics for a Global Design Software Company',
    challenge:
      'Unreliable, error-prone, non-idempotent data warehouse jobs were creating inconsistent reporting, slow SQL layers, multiple versions of truth, flawed outlier handling, and missing management KPIs.',
    solution:
      'Redesigned DWH jobs and reporting layers to be idempotent, accurate, and faster, introduced a single version of truth for metrics, automated reusable warehouse layers, applied sound statistical outlier handling, and rebuilt management reporting around the KPIs that matter.',
    metrics: [
      { label: 'DWH Jobs', value: 'Reliable' },
      { label: 'KPI Layer', value: 'Accurate' }
    ],
    tags: [
      'Design Software',
      'DWH Optimization',
      'KPI Analytics',
      'Single Version of Truth',
      'Outlier Methods'
    ],
    icon: Database
  },
  /*
  {
    id: 'ite-gov-data-infrastructure',
    category: 'Cloud Infrastructure',
    title: 'National Scale Big Data Infrastructure Modernization',
    challenge:
      'Consolidating, processing, and governing heterogeneous, high-volume state registries and public administration data streams while maintaining absolute data privacy and sovereign security standards.',
    solution:
      'Architected and implemented a secure, resilient big data analytics foundation within the state cloud environment, optimizing data ingestion pipelines, access control matrices, and automated compliance auditing.',
    metrics: [
      { label: 'Infrastructure', value: 'Secure' },
      { label: 'Data Platform', value: 'Sovereign' }
    ],
    tags: [
      'Kancelarija za IT',
      'eUprava',
      'State-Scale Data',
      'Sovereign Cloud',
      'Data Modernization'
    ],
    icon: ShieldCheck,
    image: '/images/kancelarija-za-it.png'
  },
  */
  {
    id: 'asml-data-modernization',
    category: 'Cloud Infrastructure',
    title:
      'Cloudera-to-Azure Migration for One of the Largest Semiconductor Companies',
    challenge:
      'Moving mission-critical industrial data workloads off an on-premises Cloudera estate while preserving reliability, lineage, and processing standards for complex engineering analytics.',
    solution:
      'Planned and executed the migration path to Azure and Databricks, redesigning core data layouts and Spark processing patterns for scalable cloud lakehouse operations.',
    metrics: [
      { label: 'Platform Shift', value: 'Azure' },
      { label: 'Lakehouse', value: 'Databricks' }
    ],
    tags: [
      'Cloudera Migration',
      'Microsoft Azure',
      'Databricks',
      'Semiconductor Data',
      'Lakehouse'
    ],
    icon: Cloud,
    placeholderLabel: 'CHIP INFRA'
  },
  {
    id: 'a1-serbia-dwh-migration',
    category: 'Migration',
    title: 'Oracle-to-DataSliv Migration for a Large Balkan Telco Operator',
    challenge:
      'Modernizing an on-premises Oracle data warehouse environment where slow analytical queries, brittle ingestion workflows, and limited governance made daily reporting difficult to trust.',
    solution:
      'Migrated core ingestion, storage, analytics, and governance workloads from Oracle on-premises systems to a DataSliv deployment designed for telecom-scale operational data.',
    metrics: [
      { label: 'Query Speedup', value: '5x Fast' },
      { label: 'Data Sovereignty', value: '100%' }
    ],
    tags: [
      'DataSliv',
      'Oracle Migration',
      'Large Telco',
      'On-Premises',
      'Telecom Analytics'
    ],
    icon: ShieldCheck,
    placeholderLabel: 'BALKAN TELCO'
  },
  {
    id: 'banca-intesa-compliance-lakehouse',
    category: 'Banking & Finance',
    title:
      'Strict Regulatory Compliance Big Data Lake for a Large Bank With 2M+ Customers',
    challenge:
      'Architecting and scaling a massive core financial data lake deployed on GCP while adhering to the absolute strictest central banking regulatory standards.',
    solution:
      'Deployed expert data engineering patterns to enforce total pipeline transparency, multi-layered data auditing, and native NBS/EBA compliance frameworks.',
    metrics: [
      { label: 'Banking SLA', value: '99.99%' },
      { label: 'Regulatory Audit', value: 'Passed' }
    ],
    tags: [
      'Financial Data Lake',
      'GCP Architecture',
      'Banking Compliance',
      '2M+ Customers',
      'NBS / EBA',
      'Data Auditing'
    ],
    icon: Landmark,
    placeholderLabel: 'LARGE BANK'
  },
  {
    id: 'yettel-kickoff-consulting',
    category: 'Migration',
    title: 'Data Lake Maintenance for a Large Telco',
    challenge:
      'Keeping a high-volume telecom data lake stable, observable, and ready for daily analytics workloads as ingestion volume and downstream reporting demands increased.',
    solution:
      'Provided ongoing data lake maintenance, pipeline hardening, performance tuning, and operational support so internal teams could rely on predictable, fresh datasets.',
    metrics: [
      { label: 'Data Lake', value: 'Maintained' },
      { label: 'Pipeline Ops', value: 'Stable' }
    ],
    tags: [
      'Large Telco',
      'Data Lake Maintenance',
      'Pipeline Operations',
      'Performance Tuning',
      'Operational Support'
    ],
    icon: Database,
    placeholderLabel: 'ENTERPRISE DWH'
  }
];
